import React, { useMemo, useState, useEffect, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { useTranslation } from "react-i18next";
import "../../styles/components/Editor.scss";
import "../../styles/components/EditorModal.scss";
import MultiSizeDropdown from "../MultiSizeDropDown.js";
import BannerTypeFilter from "../BannerTypeFilter.js";
import SimpleInput from "../SimpleInput.js";
import { getSizesData, copyBanner, saveEmail } from "../../services/apis.js";
import Exclam from "../../images/exclam.png";
import Tick from "../../images/tick.png";
import ToggleOnDisabled from "../../images/Toggle_on_disabled.svg";
import ToggleOffDisabled from "../../images/Toggle_off_disabled.svg";
import ValueContext from "../../ContextAPi/appContext.jsx";
import { extractInitialSizeFromAssetName } from "../../Uitls/DetailsHelpers.js";
import { nameValidation } from "../../Uitls/Util.js";
import { logoutUser } from "../../keycloak/keyCloakConfig.js";
import warning from "../../images/warning.svg";

/* ---------------- Custom tooltip (line-wrap + proper placement) --------- */

const CustomTooltip = withStyles(() => ({
  tooltip: {
    maxWidth: 140,
    whiteSpace: "normal",
    fontSize: 11,
    padding: "6px 8px",
  },
}))(function CustomTooltipComponent(props) {
  return (
    <Tooltip
      arrow
      placement="bottom-end"
      PopperProps={{
        modifiers: {
          offset: {
            enabled: true,
            offset: "0,6",
          },
        },
      }}
      {...props}
    />
  );
});

/* ---------------- helpers ---------------- */

const formatToday = () =>
  new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

const DOC_ID_REGEX = /^[A-Z]{2}-[A-Z]{3,4}-\d{3,6}$/;
const API_SUCCESS_TEXT = "This ID is not in Use.";

const parseSizesResponse = (dataObj) => {
  const labels = [];
  const labelToInfo = new Map();
  if (!dataObj || typeof dataObj !== "object") return { labels, labelToInfo };

  for (const [srcId, raw] of Object.entries(dataObj)) {
    const [h, w] = String(raw)
      .split(/[×x]/i)
      .map((n) => Number(n));
    if (!Number.isFinite(w) || !Number.isFinite(h)) continue;
    const width = w;
    const height = h;
    const label = `${width}×${height}`;
    labels.push(label);
    labelToInfo.set(label, { sourceAssetId: srcId, width, height });
  }
  return { labels, labelToInfo };
};

const copyBannerAsync = (payload) =>
  new Promise((resolve) => {
    copyBanner(payload, (resp, errResp, errorCode) => {
      if (resp && resp.status >= 200 && resp.status < 300) {
        resolve({ ok: true, resp, payload });
      } else {
        resolve({
          ok: false,
          errorCode: errorCode ?? errResp?.status ?? 500,
          err: errResp,
          payload,
        });
      }
    });
  });

const makeAssetName = ({ baseName, width, height, sizeLabel }) => {
  const trimmed = String(baseName || "").trim();
  const base = trimmed || "Banner copy";
  if (Number.isFinite(Number(width)) && Number.isFinite(Number(height))) {
    return `${base}_${width}x${height}`;
  }
  const normalizedLabel = String(sizeLabel || "")
    .replace(/[Ã×]/g, "x")
    .replace(/\s+/g, "");
  return `${base}_${normalizedLabel}`;
};

export default function CopyModal({
  open,
  onClose,
  onCreate,
  createdByName = (typeof window !== "undefined" &&
    localStorage.getItem("currentUserName")) ||
    "-",
  validatePromomats,
  parentDamDocNumber,
  isAnimated,
  isOneTimeContent = false,
  isOneTimeBanner = false,
  assetType = "BANNER",
  brandId,
  brandName,
  marketCode,
  marketName,
  parentAssetName,
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const appData = useContext(ValueContext);
  const createdOn = useMemo(() => formatToday(), []);
  const [submitting, setSubmitting] = useState(false);
  const [selectOnetimecontent, setSelectOnetimecontent] = useState(true);

  // async safety
  const isMountedRef = useRef(false);
  const validationReqIdRef = useRef(0);
  const sizesReqIdRef = useRef(0);
  const TIP_TYPE_FETCH = "Enter PromoMats Doc ID to fetch Banner Type";
  const TIP_SIZE_FETCH = "Enter PromoMats Doc ID to fetch Banner Size";
  const TIP_RESTRICT =
    "Copy from Animated to Static is restricted due to design limitations. Copy from Static to Animated is supported.";

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      validationReqIdRef.current++;
      sizesReqIdRef.current++;
      cancelTypingDelay();
    };
  }, []);

  const safeSet = (fn) => {
    if (isMountedRef.current) fn();
  };
  const preferredSizeLabel = useMemo(
    () => extractInitialSizeFromAssetName(parentAssetName),
    [parentAssetName],
  );
  const [isDelaying, setIsDelaying] = useState(false);
  const typingDelayRef = useRef(null);
  const lastValidatedIdRef = useRef("");

  const cancelTypingDelay = () => {
    if (typingDelayRef.current) {
      clearTimeout(typingDelayRef.current);
      typingDelayRef.current = null;
    }
    safeSet(() => setIsDelaying(false));
  };

  const [state, setState] = useState({
    docId: "",
    bannerType: "",
    bannerSize: [],
  });

  const [openTypeTip, setOpenTypeTip] = useState(false);
  const [openSizeTip, setOpenSizeTip] = useState(false);
  const [openTypeRestrict, setOpenTypeRestrict] = useState(false);
  const [validation, setValidation] = useState({ status: "idle", message: "" });
  const [meta, setMeta] = useState({
    name: "",
    primaryProduct: "",
    primaryCountry: "",
  });

  const [dropdownItems, setDropdownItems] = useState([]);
  const [sizesMap, setSizesMap] = useState(new Map());
  const [sizesFetched, setSizesFetched] = useState(false);
  const controlsDisabled = !(validation.status === "available");
  const bannerTypeDisabled = controlsDisabled || !!isAnimated;
  const hasRealName = !!meta?.name;

  useEffect(() => {
    if (open) {
      setSelectOnetimecontent(!!isOneTimeContent);
    } else {
      cancelTypingDelay();
      safeSet(() => {
        setValidation({ status: "idle", message: "" });
        setMeta({ name: "", primaryProduct: "", primaryCountry: "" });
        setDropdownItems([]);
        setSizesMap(new Map());
        setSizesFetched(false);
        setSubmitting(false);
        setState({ docId: "", bannerType: "", bannerSize: [] });
        setSelectOnetimecontent(false);
      });
    }
  }, [open, isOneTimeContent]);

  useEffect(() => {
    if (!controlsDisabled && !state.bannerType) {
      safeSet(() =>
        setState((s) => ({
          ...s,
          bannerType: isAnimated ? "animated" : "static",
        })),
      );
    }
  }, [controlsDisabled, isAnimated, state.bannerType]);

  useEffect(() => {
    if (!open) return;
    safeSet(() =>
      setState((s) => ({
        ...s,
        bannerType: isAnimated ? "animated" : "static",
      })),
    );
  }, [open, isAnimated]);

  useEffect(() => {
    if (
      open &&
      validation.status === "available" &&
      !sizesFetched &&
      parentDamDocNumber
    ) {
      fetchSizesAndPrefill(parentDamDocNumber);
    }
  }, [open, validation.status, sizesFetched, parentDamDocNumber]);

  // handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "docId") {
      cancelTypingDelay();
      const up = (value || "").toUpperCase();
      safeSet(() => {
        setState((s) => ({ ...s, docId: up }));
        if (!value) {
          setValidation({ status: "idle", message: "" });
          lastValidatedIdRef.current = "";
        }
        setMeta({ name: "", primaryProduct: "", primaryCountry: "" });
        setDropdownItems([]);
        setSizesMap(new Map());
        setSizesFetched(false);
        setState((s) => ({ ...s, bannerSize: [] }));
      });
    } else if (name === "bannerSize") {
      safeSet(() => setState((s) => ({ ...s, bannerSize: value || [] })));
    } else {
      safeSet(() => setState((s) => ({ ...s, [name]: value })));
    }
  };

  const handleBannerTypeChange = (val) => {
    if (!val || val.disabled) return;
    if (isAnimated && val.id === "static") return;
    safeSet(() => setState((s) => ({ ...s, bannerType: val.id })));
  };

  const runValidation = (rawId, done = () => {}) => {
    const compact = (rawId || "").toUpperCase().trim().replace(/\s+/g, "");
    const myReq = ++validationReqIdRef.current;

    if (!compact || !DOC_ID_REGEX.test(compact)) {
      safeSet(() => {
        setValidation({
          status: "error",
          message: "Please enter a correct doc ID.",
        });
        setMeta({ name: "", primaryProduct: "", primaryCountry: "" });
      });
      done(false);
      return;
    }

    safeSet(() => setValidation({ status: "checking", message: "" }));
    validatePromomats(compact, (resp, err, errorCode) => {
      if (!isMountedRef.current || myReq !== validationReqIdRef.current) return;
      const statusCode =
        errorCode ??
        resp?.status ??
        err?.response?.status ??
        err?.status ??
        resp?.data?.statusCode;

      // 🔐 If 401 → log out and DO NOT show "Validation failed"
      // if (statusCode === 401) {
      //   logoutUser();
      //   return;
      // }
      const apiMsg = (resp?.data?.message || "").trim();
      const isOk = apiMsg === API_SUCCESS_TEXT;

      safeSet(() => {
        setValidation({
          status: isOk ? "available" : "error",
          message: apiMsg || (isOk ? API_SUCCESS_TEXT : "Validation failed"),
        });

        if (isOk) {
          const m = resp?.data?.data || {};
          setMeta({
            name: m?.name || "",
            primaryProduct: m?.brandName || "",
            primaryCountry: m?.countryName || "",
          });
          lastValidatedIdRef.current = compact;
        } else {
          setMeta({ name: "", primaryProduct: "", primaryCountry: "" });
          lastValidatedIdRef.current = "";
        }
      });

      done(isOk);
    });
  };

  // const runValidation = (rawId, done = () => {}) => {
  //   const compact = (rawId || "").toUpperCase().trim().replace(/\s+/g, "");
  //   const myReq = ++validationReqIdRef.current;

  //   if (!compact || !DOC_ID_REGEX.test(compact)) {
  //     safeSet(() => {
  //       setValidation({
  //         status: "error",
  //         message: "Please enter a correct doc ID.",
  //       });
  //       setMeta({ name: "", primaryProduct: "", primaryCountry: "" });
  //     });
  //     done(false);
  //     return;
  //   }

  //   safeSet(() => setValidation({ status: "checking", message: "" }));

  //   validatePromomats(compact, (resp) => {
  //     if (!isMountedRef.current || myReq !== validationReqIdRef.current) return;

  //     const apiMsg = (resp?.data?.message || "").trim();
  //     const isOk = apiMsg === API_SUCCESS_TEXT;

  //     safeSet(() => {
  //       setValidation({
  //         status: isOk ? "available" : "error",
  //         message: apiMsg || (isOk ? API_SUCCESS_TEXT : "Validation failed"),
  //       });

  //       if (isOk) {
  //         const m = resp?.data?.data || {};
  //         setMeta({
  //           name: m?.name || "",
  //           primaryProduct: m?.brandName || "",
  //           primaryCountry: m?.countryName || "",
  //         });
  //         lastValidatedIdRef.current = compact;
  //       } else {
  //         setMeta({ name: "", primaryProduct: "", primaryCountry: "" });
  //         lastValidatedIdRef.current = "";
  //       }
  //     });

  //     done(isOk);
  //   });
  // };

  const fetchSizesAndPrefill = (docNumber) => {
    const myReq = ++sizesReqIdRef.current;
    getSizesData(docNumber, (response) => {
      if (!isMountedRef.current || myReq !== sizesReqIdRef.current) return;

      if (response && response.status === 200 && response.data) {
        const { labels, labelToInfo } = parseSizesResponse(response.data);
        const orderedLabels =
          preferredSizeLabel && labels.includes(preferredSizeLabel)
            ? [
                preferredSizeLabel,
                ...labels.filter((l) => l !== preferredSizeLabel),
              ]
            : labels;

        safeSet(() => {
          setDropdownItems(orderedLabels);
          setSizesMap(labelToInfo);
          setState((s) => ({ ...s, bannerSize: orderedLabels }));
          setSizesFetched(true);
        });
      } else {
        safeSet(() => {
          setDropdownItems([]);
          setSizesMap(new Map());
          setState((s) => ({ ...s, bannerSize: [] }));
          setSizesFetched(false);
        });
      }
    });
  };

  useEffect(() => {
    if (!open) return;
    const id = state.docId.trim();
    if (!id) {
      cancelTypingDelay();
      safeSet(() => {
        setValidation({ status: "idle", message: "" });
        lastValidatedIdRef.current = "";
      });
      return;
    }

    safeSet(() => setIsDelaying(true));
    typingDelayRef.current = setTimeout(() => {
      typingDelayRef.current = null;
      if (
        lastValidatedIdRef.current === id &&
        validation.status === "available"
      ) {
        safeSet(() => setIsDelaying(false));
        return;
      }
      if (!DOC_ID_REGEX.test(id.toUpperCase())) {
        safeSet(() => {
          setValidation({
            status: "error",
            message: "Please enter a correct doc ID.",
          });
          setIsDelaying(false);
        });
        return;
      }
      runValidation(id, () => {
        safeSet(() => setIsDelaying(false));
      });
    }, 2000);
    return () => {
      cancelTypingDelay();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.docId, open]);

  const BANNER_TYPE_OPTIONS = useMemo(
    () => [
      { id: "static", label: "Static", disabled: !!isAnimated },
      { id: "animated", label: "Animated", disabled: false },
    ],
    [isAnimated],
  );

  const clearDocId = () => {
    cancelTypingDelay();
    safeSet(() => {
      setState({ docId: "", bannerType: "", bannerSize: [] });
      setValidation({ status: "idle", message: "" });
      setMeta({ name: "", primaryProduct: "", primaryCountry: "" });
      setDropdownItems([]);
      setSizesMap(new Map());
      setSizesFetched(false);
      lastValidatedIdRef.current = "";
      validationReqIdRef.current++;
      sizesReqIdRef.current++;
    });
  };

  const handleCreate = async () => {
    const id = state.docId.trim();

    if (validation.status !== "available") {
      runValidation(id, (ok) => {
        if (ok) fetchSizesAndPrefill(id);
      });
      return;
    }

    if (!state.bannerSize || state.bannerSize.length === 0) {
      safeSet(() =>
        setValidation({
          status: "error",
          message: "Please select at least one banner size.",
        }),
      );
      return;
    }

    const isAnim = state.bannerType === "animated";
    const baseName =
      meta.name || state.docId || parentDamDocNumber || "Banner copy";

    const payloads = [];
    for (const label of state.bannerSize) {
      const info = sizesMap.get(label);
      if (!info) continue;

      const { sourceAssetId, width, height } = info;
      const assetName = makeAssetName({
        baseName,
        width,
        height,
        sizeLabel: label,
      });

      if (!nameValidation(assetName, "Banner")) {
        return;
      }

      payloads.push({
        assetType,
        brandName,
        brandId,
        marketCode,
        marketName,
        isAnimated: isAnim,
        isOneTimeBanner: Boolean(isOneTimeBanner),
        oneTimeAsset: selectOnetimecontent,
        damDocNumber: id,
        width: String(width),
        height: String(height),
        assetName,
        sourceAssetId,
      });
    }

    if (payloads.length === 0) {
      safeSet(() =>
        setValidation({
          status: "error",
          message: "Selected sizes are invalid. Please reselect.",
        }),
      );
      return;
    }

    safeSet(() => setSubmitting(true));

    const results = [];
    const email =
      typeof window !== "undefined"
        ? window.localStorage.getItem("userEmail")
        : null;

    for (const p of payloads) {
      // eslint-disable-next-line no-await-in-loop
      const r = await copyBannerAsync(p);
      results.push(r);
      if (!isMountedRef.current) break;
      if (r.ok && email) {
        const createdAssetId = r?.resp?.data?.data?.assetId;
        if (createdAssetId) {
          const emailPayload = {
            assetId: createdAssetId,
            email,
            update: false,
          };
          saveEmail(emailPayload, (response, err, errorCode) => {
            if (err) {
              console.error(
                "Failed to save user email on create (assetId:",
                createdAssetId,
                "):",
                err,
                "errorCode:",
                errorCode,
              );
            }
          });
        }
      }
    }

    if (!isMountedRef.current) return;

    const success = results.filter((r) => r.ok);
    const failures = results.filter((r) => !r.ok);

    if (success.length > 0) {
      safeSet(() => setSubmitting(false));

      const first = success[0];
      const newAssetId = first?.resp?.data?.data?.assetId;

      if (typeof onCreate === "function") {
        onCreate(success);
      }
      if (typeof onClose === "function") {
        onClose();
      }
      if (newAssetId) {
        appData.setFlagEditFlow(true);

        history.push({
          pathname: `/banner-editor/${newAssetId}`,
          state: {
            editClicked: true,
            newBanner: false,
            onetimeBanner: selectOnetimecontent,
            createBanner: assetType,
            brandName: first?.payload?.brandName || brandName,
            countryName: first?.payload?.marketName || marketName,
          },
        });
      }

      return;
    }

    const errText =
      failures.length === results.length
        ? "Copy failed for all selected sizes."
        : `Copied ${success.length} / ${results.length} sizes. Some failed.`;

    safeSet(() => {
      setSubmitting(false);
      setValidation({ status: "error", message: errText });
    });
  };

  const firstSelectedLabel = useMemo(() => {
    const list = (state.bannerSize || []).map((x) =>
      typeof x === "string" ? x : (x?.label ?? ""),
    );
    return list[0] || "";
  }, [state.bannerSize]);
  const hasSizes =
    Array.isArray(state.bannerSize) && state.bannerSize.length > 0;

  const canMakeCopy =
    validation.status === "available" && hasSizes && !submitting;
  const showTypeDisabledTip = controlsDisabled;
  const showSizeDisabledTip = controlsDisabled;
  const showTypeRestrictTip = !controlsDisabled && !!isAnimated;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="form-dialog-title"
      className="newTemplateModal"
    >
      {submitting && (
        <>
          <div className="modal-backdrop fade show" />
          <CircularProgress size="30px" className="customLoadermodules" />
        </>
      )}

      <div className="row newBnrModal">
        {/* LEFT: Metadata */}
        <div
          className="baseOnModalHeading col-md-6 createbannerhead customScrollBar"
          style={{ fontFamily: "'Nunito Sans', sans-serif" }}
        >
          <p>{t("METADATA INFORMATION")}</p>

          <div className="details" style={{ flexDirection: "column" }}>
            <p className="sourcetag">Source</p>
            <p className="tagDescription" style={{ paddingLeft: "0px" }}>
              PromoMats
            </p>
          </div>

          <hr className="custom-hr" />

          <div className="details" style={{ flexDirection: "column" }}>
            <p className="sourcetag">Created by</p>
            <p className="tagDescription" style={{ paddingLeft: "0px" }}>
              {createdByName}
            </p>
          </div>

          <div className="details" style={{ flexDirection: "column" }}>
            <p className="sourcetag">Created on</p>
            <p className="tagDescription" style={{ paddingLeft: "0px" }}>
              {createdOn}
            </p>
          </div>

          <div className="details" style={{ flexDirection: "column" }}>
            <p className="sourcetag">Name</p>

            <p className="tagDescription" style={{ paddingLeft: "0px" }}>
              {hasRealName ? (
                <CustomTooltip title={meta.name}>
                  <span className="metaNameEllipsis">{meta.name}</span>
                </CustomTooltip>
              ) : (
                <span className="metaNameEllipsis">-</span>
              )}
            </p>
          </div>

          <div className="details" style={{ flexDirection: "column" }}>
            <p className="sourcetag">Primary Product</p>
            <p className="tagDescription" style={{ paddingLeft: "0px" }}>
              {meta.primaryProduct || "-"}
            </p>
          </div>

          <div className="details" style={{ flexDirection: "column" }}>
            <p className="sourcetag">Primary Country</p>
            <p className="tagDescription" style={{ paddingLeft: "0px" }}>
              {meta.primaryCountry || "-"}
            </p>
          </div>
        </div>

        {/* RIGHT: Form */}
        <div
          className="col-md-6 selectBrands customScrollBar"
          style={{
            marginTop: 15,
            paddingTop: 15,
            paddingBottom: 0,
          }}
        >
          <div style={{ position: "relative" }}>
            <SimpleInput
              id="promomats-docid"
              name="docId"
              label="PromoMats Doc ID"
              placeholder="Enter PromoMats Doc ID"
              required
              value={state.docId}
              onChange={handleChange}
              onFocus={(e) => {
                const len = e.target.value.length;
                e.target.setSelectionRange(len, len);
              }}
              inputProps={{ "aria-label": "PromoMats Doc ID" }}
            />

            {state.docId && (
              <div
                style={{
                  position: "absolute",
                  right: 6,
                  top: "50%",
                  transform: "translateY(-50%)",
                  padding: 4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 24,
                  height: 24,
                }}
              >
                {isDelaying || validation.status === "checking" ? (
                  <CircularProgress size={16} thickness={5} />
                ) : (
                  <IconButton
                    aria-label="clear PromoMats Doc ID"
                    onClick={clearDocId}
                    size="small"
                    style={{ padding: 4 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </div>
            )}
          </div>

          {/* Validation message */}
          <div style={{ minHeight: validation.message ? 18 : 6 }}>
            {validation.message ? (
              <span
                style={{
                  color:
                    validation.status === "available" ? "#378A05" : "#C50F3C",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <img
                  src={validation.status === "available" ? Tick : Exclam}
                  alt={
                    validation.status === "available" ? "validated" : "warning"
                  }
                  style={{ width: 8, height: 8, display: "inline-block" }}
                />
                {validation.message}
              </span>
            ) : null}
          </div>

          {/* Banner Type + tooltips */}
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginTop: 5,
            }}
          >
            <div
              className={controlsDisabled ? "control-disabled" : ""}
              style={{ flex: 1 }}
            >
              <BannerTypeFilter
                isAnimated={!!isAnimated}
                disabled={bannerTypeDisabled}
                value={
                  controlsDisabled
                    ? null
                    : isAnimated
                      ? { id: "animated", label: "Animated" }
                      : BANNER_TYPE_OPTIONS.find(
                          (o) => o.id === state.bannerType,
                        ) || {
                          id: "static",
                          label: "Static",
                        }
                }
                onChange={handleBannerTypeChange}
              />
            </div>

            {/* 1) Disabled-because-no-doc tooltip */}
            {showTypeDisabledTip && (
              <ClickAwayListener onClickAway={() => setOpenTypeTip(false)}>
                <div className="infoIcon">
                  <CustomTooltip title={TIP_TYPE_FETCH} open={openTypeTip}>
                    <IconButton
                      size="small"
                      onClick={() => setOpenTypeTip((v) => !v)}
                      aria-label="Banner Type help"
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </CustomTooltip>
                </div>
              </ClickAwayListener>
            )}

            {/* 2) Restriction tooltip */}
            {showTypeRestrictTip && (
              <ClickAwayListener onClickAway={() => setOpenTypeRestrict(false)}>
                <div className="infoIcon">
                  <CustomTooltip title={TIP_RESTRICT} open={openTypeRestrict}>
                    <IconButton
                      size="small"
                      onClick={() => setOpenTypeRestrict((v) => !v)}
                      aria-label="Banner Type restriction info"
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </CustomTooltip>
                </div>
              </ClickAwayListener>
            )}
          </div>

          {/* Banner Sizes + tooltip */}
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 6,
              marginTop: 5,
            }}
          >
            <div
              className={controlsDisabled ? "control-disabled" : ""}
              style={{ flex: 1 }}
            >
              <MultiSizeDropdown
                options={{
                  disabled: controlsDisabled,
                  value: state.bannerSize,
                  name: "bannerSize",
                  label: (
                    <>
                      Banner Sizes <span style={{ color: "red" }}>*</span>
                    </>
                  ),
                  items: dropdownItems,
                  displayValue: firstSelectedLabel,
                  handleChange: handleChange,
                  className: "testBannersize",
                }}
              />
            </div>

            {showSizeDisabledTip && (
              <ClickAwayListener onClickAway={() => setOpenSizeTip(false)}>
                <div className="infoIcon2">
                  <CustomTooltip title={TIP_SIZE_FETCH} open={openSizeTip}>
                    <IconButton
                      size="small"
                      onClick={() => setOpenSizeTip((v) => !v)}
                      aria-label="Banner Size help"
                    >
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </CustomTooltip>
                </div>
              </ClickAwayListener>
            )}
          </div>

          <div
            className="otContentText makecopyOtc"
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <span>One time content</span>
            {/* <img
              src={selectOnetimecontent ? ToggleOnDisabled : ToggleOffDisabled}
              alt="One time content disabled"
              style={{ display: "block" }}
            /> */}
            <Tooltip
              title={"All banner created are One time content banners"}
            >
              <img src={warning} alt="Warning" />
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Footer */}
      <DialogActions
        className="modalActions templateBtn"
        style={{ justifyContent: "flex-end" }}
      >
        <Button
          onClick={onClose}
          color="primary"
          className="cancelBtntemp"
          disabled={submitting}
          style={{ marginRight: 16 }}
        >
          {t("Cancel") || "Cancel"}
        </Button>

        <Button
          onClick={handleCreate}
          className="temp-btn"
          autoFocus
          disabled={!canMakeCopy}
          style={{ fontFamily: "'Nunito Sans', sans-serif" }}
        >
          {submitting
            ? t("Copying...") || "Copying..."
            : t("Make a Copy") || "Make a Copy"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
